/**
 * D9: Oefenlab UI State
 * Source: src/learningLab/LearningLabPage.tsx
 */

import { USE_BACKEND, STORAGE_KEY, readJson, writeJson, removeKey } from '../helpers';
const FLUENCY_INTRO_KEY = 'fluency_intro_seen';
const SPRINT_INTRO_KEY = 'sprint_intro_seen';
const FREE_PRACTICE_INTRO_KEY = 'oefenlab_free_practice_intro_seen';
const CHALLENGE_INTRO_KEY = 'oefenlab_challenge_intro_seen';
const LAST_OEFENLAB_SESSION_KEY = 'oefenlab_last_session';
const NEWLY_UNLOCKED_CHALLENGE_KEY = 'oefenlab_newly_unlocked_challenge';
const PERSONAL_RECORDS_KEY = 'oefenlab_personal_records';

/** HC-1 fix: localStorage key for klascode banner — now uses STORAGE_KEY prefix */
const KLASCODE_BANNER_KEY = `${STORAGE_KEY}_klascode_banner_dismissed`;
export interface PersonalRecords {
  fluency?: {
    fastestAvgMs: number;
    longestStreak: number;
    bestScore: number;
  };
  sprint?: {
    bestScore: number;
    totalSessions: number;
  };
  challenge?: {
    highestTier: number;
    bestScore: number;
    totalCompleted: number;
  };
}
export async function getFluencyIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    return localStorage.getItem(FLUENCY_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}
export async function setFluencyIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    if (seen) {
      localStorage.setItem(FLUENCY_INTRO_KEY, '1');
    } else {
      localStorage.removeItem(FLUENCY_INTRO_KEY);
    }
  } catch {

    // silent
  }
}
export async function getSprintIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    return localStorage.getItem(SPRINT_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}
export async function setSprintIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    if (seen) {
      localStorage.setItem(SPRINT_INTRO_KEY, '1');
    } else {
      localStorage.removeItem(SPRINT_INTRO_KEY);
    }
  } catch {

    // silent
  }
}
export async function getFreePracticeIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    return localStorage.getItem(FREE_PRACTICE_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}
export async function setFreePracticeIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  writeJson(FREE_PRACTICE_INTRO_KEY, seen);
}
export async function getChallengeIntroSeen(): Promise<boolean> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    return localStorage.getItem(CHALLENGE_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}
export async function setChallengeIntroSeen(seen: boolean): Promise<void> {
  if (USE_BACKEND) {

    // TODO: backend call
  } else {
    if (seen) localStorage.setItem(CHALLENGE_INTRO_KEY, '1');else localStorage.removeItem(CHALLENGE_INTRO_KEY);
  }
}

/** Get klascode banner dismissed state */
export async function getKlascodeBannerDismissed(): Promise<boolean> {
  if (USE_BACKEND) {
    // TODO: backend call
    return false;
  }
  // Migration: read old key if new key not found
  const newVal = localStorage.getItem(KLASCODE_BANNER_KEY);
  if (newVal !== null) return newVal === 'true';
  const oldVal = localStorage.getItem('klascode_banner_dismissed');
  if (oldVal === 'true') {
    // Migrate to new key
    localStorage.setItem(KLASCODE_BANNER_KEY, 'true');
    localStorage.removeItem('klascode_banner_dismissed');
    return true;
  }
  return false;
}

/** Set klascode banner dismissed state */
export async function setKlascodeBannerDismissed(dismissed: boolean): Promise<void> {
  if (USE_BACKEND) {

    // TODO: backend call
  } else {
    if (dismissed) localStorage.setItem(KLASCODE_BANNER_KEY, 'true');else localStorage.removeItem(KLASCODE_BANNER_KEY);
    // Clean up old key if present
    localStorage.removeItem('klascode_banner_dismissed');
  }
}

/** Get the last active session state for Oefenlab */
export async function getLastOefenlabSession(): Promise<{
  modeId: string;
  familyId?: string;
  timestamp: number;
  score?: number;
} | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson(LAST_OEFENLAB_SESSION_KEY, null);
}

/** Save the last oefenlab session details */
export async function saveLastOefenlabSession(info: {
  modeId: string;
  familyId?: string;
  timestamp: number;
  score?: number;
}): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  writeJson(LAST_OEFENLAB_SESSION_KEY, info);
}

/** Get newly unlocked challenge familyId */
export async function getNewlyUnlockedChallenge(): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson(NEWLY_UNLOCKED_CHALLENGE_KEY, null);
}

/** Set newly unlocked challenge familyId */
export async function setNewlyUnlockedChallenge(familyId: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  writeJson(NEWLY_UNLOCKED_CHALLENGE_KEY, familyId);
}

/** Clear newly unlocked challenge */
export async function clearNewlyUnlockedChallenge(): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  removeKey(NEWLY_UNLOCKED_CHALLENGE_KEY);
}

/** Get personal records for oefenlab modes */
export async function getPersonalRecords(): Promise<PersonalRecords> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  return readJson<PersonalRecords>(PERSONAL_RECORDS_KEY, {});
}

/** Save personal records for oefenlab modes */
export async function savePersonalRecords(records: PersonalRecords): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  writeJson(PERSONAL_RECORDS_KEY, records);
}